import { Controller } from "../vendor/stimulus.js";

const HAS_SCROLL_CLASS = "has-scroll-bar";
const SCROLLED_CLASS = "scrolled";
const SCROLLED_END_CLASS = "scrolled-to-end";
const TOUCH_INTERFACE_CLASS = "is-touch-interface";
const POSITION_EPSILON = 1;
const SNAP_DISABLE_DURATION = 500;
const SCROLL_STEP_RATIO = 0.85;

export default class extends Controller {
  static targets = [
    "view",
    "prevButton",
    "nextButton"
  ];

  connect() {
    this.viewElement = this.hasViewTarget ? this.viewTarget : null;
    if (!this.viewElement) return;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handlePrevClick = () => this.scrollByStep(-1);
    this.handleNextClick = () => this.scrollByStep(1);

    this.viewElement.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleResize, { passive: true });

    if ("ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.viewElement);
    }

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.addEventListener("click", this.handlePrevClick);
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.addEventListener("click", this.handleNextClick);
    }

    this.syncTouchInterfaceState();
    this.evaluateScrollableState();
  }

  disconnect() {
    if (this.viewElement) {
      this.viewElement.removeEventListener("scroll", this.handleScroll);
    }

    window.removeEventListener("resize", this.handleResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.removeEventListener("click", this.handlePrevClick);
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.removeEventListener("click", this.handleNextClick);
    }

    this.viewElement = null;
  }

  handleScroll() {
    this.updateScrollClasses();
  }

  handleResize() {
    this.evaluateScrollableState();
  }

  evaluateScrollableState() {
    if (!this.viewElement) return;

    const hasScroll = this.hasOverflow(this.viewElement);
    this.element.classList.toggle(HAS_SCROLL_CLASS, hasScroll);

    if (!hasScroll) {
      this.element.classList.remove(SCROLLED_CLASS, SCROLLED_END_CLASS);
      return;
    }

    this.updateScrollClasses();
  }

  updateScrollClasses() {
    if (!this.viewElement) return;

    const atStart = this.isAtStart(this.viewElement);
    const atEnd = this.isAtEnd(this.viewElement);

    if (atStart) {
      this.element.classList.remove(SCROLLED_CLASS, SCROLLED_END_CLASS);
      this.updateButtonState(atStart, atEnd);
      return;
    }

    this.element.classList.add(SCROLLED_CLASS);
    this.element.classList.toggle(SCROLLED_END_CLASS, atEnd);

    this.updateButtonState(atStart, atEnd);
  }

  updateButtonState(atStart, atEnd) {
    if (this.isTouchInterface) return;

    if (this.hasPrevButtonTarget) {
      this.toggleButtonDisabled(this.prevButtonTarget, atStart);
    }

    if (this.hasNextButtonTarget) {
      this.toggleButtonDisabled(this.nextButtonTarget, atEnd);
    }
  }

  toggleButtonDisabled(button, disabled) {
    if (!button) return;
    button.classList.toggle("is-disabled", disabled);
    button.setAttribute("aria-disabled", disabled ? "true" : "false");
  }

  hasOverflow(view) {
    return this.horizontalOverflow(view) || this.verticalOverflow(view);
  }

  horizontalOverflow(view) {
    return view.scrollWidth - view.clientWidth > POSITION_EPSILON;
  }

  verticalOverflow(view) {
    return view.scrollHeight - view.clientHeight > POSITION_EPSILON;
  }

  isAtStart(view) {
    if (this.horizontalOverflow(view)) {
      return view.scrollLeft <= POSITION_EPSILON;
    }

    if (this.verticalOverflow(view)) {
      return view.scrollTop <= POSITION_EPSILON;
    }

    return true;
  }

  isAtEnd(view) {
    if (this.horizontalOverflow(view)) {
      const maxScrollLeft = view.scrollWidth - view.clientWidth;
      return view.scrollLeft >= maxScrollLeft - POSITION_EPSILON;
    }

    if (this.verticalOverflow(view)) {
      const maxScrollTop = view.scrollHeight - view.clientHeight;
      return view.scrollTop >= maxScrollTop - POSITION_EPSILON;
    }

    return true;
  }

  scrollByStep(direction = 1) {
    if (!this.viewElement) return;

    const isHorizontal = this.horizontalOverflow(this.viewElement);
    const baseSize = isHorizontal ? this.viewElement.clientWidth : this.viewElement.clientHeight;
    const step = Math.max(baseSize * SCROLL_STEP_RATIO, 1);
    const offset = step * direction;

    this.disableSnapTemporarily();

    if (isHorizontal) {
      this.viewElement.scrollBy({ left: offset, behavior: "smooth" });
    } else {
      this.viewElement.scrollBy({ top: offset, behavior: "smooth" });
    }
  }

  disableSnapTemporarily() {
    if (!this.viewElement) return;

    this.viewElement.classList.add("no-scroll-snap");

    if (this.snapTimeoutId) {
      window.clearTimeout(this.snapTimeoutId);
    }

    this.snapTimeoutId = window.setTimeout(() => {
      this.viewElement.classList.remove("no-scroll-snap");
      this.snapTimeoutId = null;
    }, SNAP_DISABLE_DURATION);
  }

  syncTouchInterfaceState() {
    this.isTouchInterface = this.detectTouchInterface();
    this.element.classList.toggle(TOUCH_INTERFACE_CLASS, this.isTouchInterface);
  }

  detectTouchInterface() {
    if (typeof window === "undefined") return false;

    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
      return true;
    }

    const nav = typeof navigator !== "undefined" ? navigator : null;

    return (
      "ontouchstart" in window ||
      (nav && (nav.maxTouchPoints > 0 || nav.msMaxTouchPoints > 0))
    );
  }
}
